import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { LegacyNotificationsAdapter } from './legacy-notifications.adapter';
import { NotifierInboxService } from './notifier-inbox.service';
import { normalizeLegacyEvent, sortNotifications, uniqueNotifications } from './notification-normalizers';
import {
  NotificationItem, NotificationSource, NotificationToast, NotificationsSourceState,
  NotifierInboxPage, NotifierPing,
} from './notification.model';

interface SourceRuntime {
  running: boolean;
  requested: boolean;
  revision: number;
  mutations: number;
  mutationTail: Promise<void>;
  desiredPages: number;
  lastSuccessAt: number;
  refreshTimer?: ReturnType<typeof setTimeout>;
}

interface PendingToast {
  readonly source: NotificationSource;
  readonly messageId?: string;
  readonly fallback: NotificationToast;
}

const PAGE_SIZE = 30;
const REFRESH_COALESCE_MS = 150;
const STALE_MS = 60_000;
const TOAST_DEDUP_MS = 5 * 60_000;
const MAX_RECENT_TOASTS = 500;
const SOURCES: readonly NotificationSource[] = ['legacy', 'notifier'];

function emptyState(): NotificationsSourceState {
  return { items: [], loading: false, loaded: false, error: null, totalCount: 0, unreadCount: 0 };
}

function freshRuntime(): SourceRuntime {
  return {
    running: false, requested: false, revision: 0, mutations: 0,
    mutationTail: Promise.resolve(), desiredPages: 1, lastSuccessAt: 0,
  };
}

/**
 * Unico stato della campanella. Le due inbox REST sono autorevoli e indipendenti.
 * Il coordinatore della sessione inoltra qui gli eventi; il trasporto non modifica i badge.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsStore {
  private readonly legacy = inject(LegacyNotificationsAdapter);
  private readonly notifier = inject(NotifierInboxService);
  private readonly legacyValue = signal<NotificationsSourceState>(emptyState());
  private readonly notifierValue = signal<NotificationsSourceState>(emptyState());
  private readonly mutationsValue = signal(0);
  private readonly operationErrorValue = signal<'mutationFailed' | null>(null);
  private readonly toastSubject = new Subject<NotificationToast>();
  private readonly sessionEnd = new Subject<void>();
  private runtime: Record<NotificationSource, SourceRuntime> = { legacy: freshRuntime(), notifier: freshRuntime() };
  private readonly recentToasts = new Map<string, number>();
  private readonly pendingToasts = new Map<string, PendingToast>();
  private readonly active = signal(false);
  private generation = 0;

  readonly legacyState = this.legacyValue.asReadonly();
  readonly sessionActive = this.active.asReadonly();
  readonly notifierState = this.notifierValue.asReadonly();
  readonly items = computed(() => sortNotifications([...this.legacyValue().items, ...this.notifierValue().items]));
  readonly unreadCount = computed(() => this.legacyValue().unreadCount + this.notifierValue().unreadCount);
  readonly loading = computed(() => this.legacyValue().loading || this.notifierValue().loading);
  readonly hasMore = computed(() => this.notifierValue().items.length < this.notifierValue().totalCount);
  readonly mutating = computed(() => this.mutationsValue() > 0);
  readonly operationError = this.operationErrorValue.asReadonly();
  readonly toastEvents$: Observable<NotificationToast> = this.toastSubject.asObservable();

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.stop();
      this.sessionEnd.complete();
      this.toastSubject.complete();
    });
  }

  start(): void {
    if (this.active()) return;
    this.active.set(true);
    this.generation++;
    this.refresh();
  }

  stop(): void {
    this.active.set(false);
    this.generation++;
    this.sessionEnd.next();
    for (const source of SOURCES) clearTimeout(this.runtime[source].refreshTimer);
    this.runtime = { legacy: freshRuntime(), notifier: freshRuntime() };
    this.pendingToasts.clear();
    this.recentToasts.clear();
    this.legacyValue.set(emptyState());
    this.notifierValue.set(emptyState());
    this.mutationsValue.set(0);
    this.operationErrorValue.set(null);
  }

  refresh(source?: NotificationSource): void {
    for (const target of source ? [source] : SOURCES) this.requestRefresh(target, false);
  }

  panelOpened(): void {
    this.refresh();
  }

  refreshIfStale(): void {
    for (const source of SOURCES) {
      if (Date.now() - this.runtime[source].lastSuccessAt >= STALE_MS) this.requestRefresh(source, false);
    }
  }

  reconnected(source: NotificationSource): void {
    this.requestRefresh(source, true);
  }

  loadMore(): void {
    if (!this.active() || !this.hasMore() || this.notifierValue().loading || this.runtime.notifier.mutations) return;
    this.runtime.notifier.desiredPages++;
    this.requestRefresh('notifier', false);
  }

  handleLegacyEvent(payload: unknown): void {
    if (!this.active()) return;
    const item = normalizeLegacyEvent(payload);
    if (item && item.isUnread && !this.legacyValue().items.some(row => row.key === item.key) && this.rememberToast(item.key)) {
      this.pendingToasts.set(item.key, {
        source: 'legacy', fallback: { key: item.key, title: item.title, body: item.body, notification: item },
      });
    }
    this.requestRefresh('legacy', true);
  }

  handleNotifierPing(ping: NotifierPing): void {
    if (!this.active()) return;
    const key = `notifier:${ping.messageId}`;
    if (ping.messageId && !this.notifierValue().items.some(item => item.messageId === ping.messageId) && this.rememberToast(key)) {
      this.pendingToasts.set(key, {
        source: 'notifier', messageId: ping.messageId,
        fallback: { key, title: ping.title, body: '' },
      });
    }
    this.requestRefresh('notifier', true);
  }

  markRead(item: NotificationItem): Promise<boolean> {
    if (!item.isUnread) return Promise.resolve(true);
    return this.mutate(item.source,
      () => item.source === 'legacy' ? this.legacy.markRead(item.id) : this.notifier.markRead(item.id),
      () => this.markLocallyRead(item.source, row => row.key === item.key));
  }

  remove(item: NotificationItem): Promise<boolean> {
    return this.mutate(item.source,
      () => item.source === 'legacy' ? this.legacy.remove(item.id) : this.notifier.remove(item.id),
      () => this.removeLocally(item.source, row => row.key === item.key));
  }

  async markAllRead(): Promise<boolean> {
    const operations = this.legacyValue().items.filter(item => item.isUnread).map(item => this.markRead(item));
    if (this.notifierValue().unreadCount > 0) {
      operations.push(this.mutate('notifier', () => this.notifier.markAllRead(), () => {
        this.markLocallyRead('notifier', () => true);
        this.notifierValue.update(state => ({ ...state, unreadCount: 0 }));
      }));
    }
    return (await Promise.all(operations)).every(Boolean);
  }

  async clear(): Promise<boolean> {
    // Lo snapshot esclude le notifiche nuove: nessun delete viene ripetuto su una lista ricaricata.
    const legacySnapshot = [...this.legacyValue().items];
    const throughId = this.notifierValue().items.reduce((max, item) => Math.max(max, item.id), 0);
    const operations = legacySnapshot.map(item => this.remove(item));
    if (throughId > 0) {
      operations.push(this.mutate('notifier', () => this.notifier.clearThrough(throughId),
        () => this.notifierValue.update(state => {
          // Il cutoff comprende anche le pagine non caricate: non sottrarre solo le righe visibili.
          const remaining = state.items.filter(item => item.id > throughId);
          return { ...state, items: remaining, totalCount: remaining.length,
            unreadCount: remaining.filter(item => item.isUnread).length };
        })));
    }
    return (await Promise.all(operations)).every(Boolean);
  }

  private requestRefresh(source: NotificationSource, coalesce: boolean): void {
    if (!this.active()) return;
    const slot = this.runtime[source];
    slot.requested = true;
    // Un evento durante la fetch richiede SEMPRE una seconda lettura dopo quella in corso.
    if (slot.running || slot.mutations) return;
    if (coalesce) {
      if (slot.refreshTimer === undefined) {
        slot.refreshTimer = setTimeout(() => {
          slot.refreshTimer = undefined;
          void this.drain(source, slot);
        }, REFRESH_COALESCE_MS);
      }
    } else {
      clearTimeout(slot.refreshTimer);
      slot.refreshTimer = undefined;
      void this.drain(source, slot);
    }
  }

  private async drain(source: NotificationSource, slot: SourceRuntime): Promise<void> {
    if (slot.running || slot.mutations || !this.active() || this.runtime[source] !== slot) return;
    const generation = this.generation;
    slot.running = true;
    while (this.isCurrent(generation) && this.runtime[source] === slot && slot.requested && !slot.mutations) {
      slot.requested = false;
      const revision = slot.revision;
      this.state(source).update(state => ({ ...state, loading: true }));
      try {
        const page = await (source === 'legacy' ? this.fetchLegacy() : this.fetchNotifier(slot, generation, revision));
        if (page && this.isCurrent(generation) && revision === slot.revision) {
          slot.lastSuccessAt = Date.now();
          this.state(source).set({ ...page, loaded: true, loading: false, error: null });
          // Se il ping è arrivato durante questa richiesta, attendi la rilettura già accodata.
          if (!slot.requested) this.flushToasts(source);
        }
      } catch {
        if (this.isCurrent(generation) && revision === slot.revision) {
          this.state(source).update(state => ({ ...state, loading: false, error: 'unavailable' }));
          this.flushToasts(source);
        }
      }
    }
    slot.running = false;
    if (this.isCurrent(generation) && this.runtime[source] === slot) {
      this.state(source).update(state => ({ ...state, loading: false }));
    }
  }

  private async fetchLegacy(): Promise<NotifierInboxPage> {
    const items = await this.read(this.legacy.list());
    return { items, totalCount: items.length, unreadCount: items.filter(item => item.isUnread).length };
  }

  private async fetchNotifier(slot: SourceRuntime, generation: number, revision: number): Promise<NotifierInboxPage | null> {
    const items: NotificationItem[] = [];
    let result: NotifierInboxPage = { items: [], totalCount: 0, unreadCount: 0 };
    // L'API usa offset: rileggere il prefisso caricato evita buchi dopo inserimenti/cancellazioni.
    for (let page = 1; page <= slot.desiredPages; page++) {
      if (!this.isCurrent(generation) || revision !== slot.revision) return null;
      result = await this.read(this.notifier.getPage(page, PAGE_SIZE));
      items.push(...result.items);
      if (page * PAGE_SIZE >= result.totalCount || result.items.length === 0) {
        slot.desiredPages = page;
        break;
      }
    }
    return { ...result, items: uniqueNotifications(items) };
  }

  private mutate(source: NotificationSource, request: () => Observable<void>, apply: () => void): Promise<boolean> {
    if (!this.active()) return Promise.resolve(false);
    const slot = this.runtime[source];
    const generation = this.generation;
    // Invalida subito le letture già partite, anche quando la mutazione aspetta il proprio turno.
    slot.revision++;
    slot.mutations++;
    this.mutationsValue.update(count => count + 1);
    this.operationErrorValue.set(null);
    const operation = slot.mutationTail.then(async () => {
      if (!this.isCurrent(generation)) return false;
      try {
        await this.read(request());
        if (!this.isCurrent(generation)) return false;
        apply();
        return true;
      } catch {
        if (this.isCurrent(generation)) this.operationErrorValue.set('mutationFailed');
        return false;
      } finally {
        slot.mutations--;
        if (this.isCurrent(generation)) {
          this.mutationsValue.update(count => count - 1);
          this.requestRefresh(source, false);
        }
      }
    });
    slot.mutationTail = operation.then(() => undefined);
    return operation;
  }

  private markLocallyRead(source: NotificationSource, predicate: (item: NotificationItem) => boolean): void {
    this.state(source).update(state => {
      const changed = state.items.filter(item => item.isUnread && predicate(item)).length;
      return {
        ...state, unreadCount: Math.max(0, state.unreadCount - changed),
        items: state.items.map(item => predicate(item) ? { ...item, isUnread: false } : item),
      };
    });
  }

  private removeLocally(source: NotificationSource, predicate: (item: NotificationItem) => boolean): void {
    this.state(source).update(state => {
      const removed = state.items.filter(predicate);
      return {
        ...state, items: state.items.filter(item => !predicate(item)),
        totalCount: Math.max(0, state.totalCount - removed.length),
        unreadCount: Math.max(0, state.unreadCount - removed.filter(item => item.isUnread).length),
      };
    });
  }

  private state(source: NotificationSource) {
    return source === 'legacy' ? this.legacyValue : this.notifierValue;
  }

  private read<T>(request: Observable<T>): Promise<T> {
    return firstValueFrom(request.pipe(takeUntil(this.sessionEnd)));
  }

  private isCurrent(generation: number): boolean {
    return this.active() && generation === this.generation;
  }

  private rememberToast(key: string): boolean {
    const now = Date.now();
    for (const [seenKey, seenAt] of this.recentToasts) {
      if (now - seenAt >= TOAST_DEDUP_MS) this.recentToasts.delete(seenKey);
    }
    if (this.recentToasts.has(key)) return false;
    if (this.recentToasts.size >= MAX_RECENT_TOASTS) {
      const oldest = this.recentToasts.keys().next().value;
      if (oldest !== undefined) {
        this.recentToasts.delete(oldest);
        this.pendingToasts.delete(oldest);
      }
    }
    this.recentToasts.set(key, now);
    return true;
  }

  private flushToasts(source: NotificationSource): void {
    for (const [key, pending] of this.pendingToasts) {
      if (pending.source !== source) continue;
      const item = this.state(source)().items.find(row =>
        pending.messageId ? row.messageId === pending.messageId : row.key === pending.fallback.key);
      this.toastSubject.next(item
        ? { key, title: item.title, body: item.body, notification: item }
        : pending.fallback);
      this.pendingToasts.delete(key);
    }
  }
}
